import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {showToast} from '@/components/Toaster/Toaster';
import amplitudeNotificationTracking from '@/services/analytics/amplitude/amplitudeNotificationTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import appContent from '@/services/content/appContent';
import notificationPermissionService from '@/services/pushNotification/notificationPermissionService';
import {checkIsShowPushPrompt} from '@/services/pushNotification/pushNotificationPromptService';
import pushNotificationService from '@/services/pushNotification/pushNotificationService';
import notificationStorageService from '@/services/storage/notificationStorageService';
import {showResetNotificationPermissionsModal} from '@/state/slices/modalSlice';
import {setPushNotificationSettingChangeInProgress} from '@/state/slices/pushNotificationSlice';

const usePushNotificationSettingsChange = ({hideFailedMessage} = {}) => {
    const dispatch = useDispatch();

    const showFailedNotification = useCallback(() => {
        if (hideFailedMessage) return;

        const {title, description, buttonText} = appContent.getUpdatePushNotificationsFailedNotification();

        showToast(title, {
            description,
            action: {
                label: buttonText,
            },
        });
    }, []);

    const handleNotificationSettingChange = useCallback(
        async (isNotificationEnabled, onNotificationDenied) => {
            dispatch(setPushNotificationSettingChangeInProgress(true));

            const trackEvent = (isSuccess) => {
                googleAnalyticsService.trackPushNotificationsSettingChanged(isNotificationEnabled, isSuccess);
                amplitudeNotificationTracking.trackPushNotificationsSettingChanged(isNotificationEnabled, isSuccess);
            };

            if (isNotificationEnabled) {
                notificationStorageService.setIsPushNotificationDisabled(false);

                const isNotificationDenied = await notificationPermissionService.checkIfNotificationIsDenied();
                const isNotificationDefault = await notificationPermissionService.checkIfNotificationIsDefault();
                const isNotificationGranted = await notificationPermissionService.checkIfNotificationIsGranted();

                if (isNotificationDenied) {
                    if (onNotificationDenied) {
                        onNotificationDenied(true);
                    }
                    dispatch(showResetNotificationPermissionsModal());
                } else if (isNotificationDefault || isNotificationGranted) {
                    try {
                        await pushNotificationService.requestSubscription();
                        trackEvent(true);
                    } catch (e) {
                        trackEvent(false);
                        showFailedNotification();
                    }
                }
            } else {
                try {
                    await pushNotificationService.disablePushNotifications();
                    trackEvent(true);
                } catch (e) {
                    trackEvent(false);
                    showFailedNotification();
                }
            }

            dispatch(setPushNotificationSettingChangeInProgress(false));
            await checkIsShowPushPrompt();
        },
        [dispatch, showFailedNotification]
    );

    return {
        handleNotificationSettingChange,
    };
};

export default usePushNotificationSettingsChange;
